import React, { useEffect, useRef, useState } from 'react';
import { Share2, MessageCircle, Send, MessageSquare, Mail, Link2, Check, MoreHorizontal } from 'lucide-react';

/**
 * Reusable share control. On devices with the Web Share API it opens the native
 * share sheet (one-click); otherwise it falls back to an in-app menu with
 * WhatsApp / Telegram / SMS / Email / Copy link.
 */
const ShareMenu = ({ title = '', text = '', url = '', testid = 'share' }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);
  const full = url ? `${text}\n${url}` : text;

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const nativeShare = async () => {
    try { await navigator.share({ title, text, url }); } catch (_) { /* user cancelled */ }
  };

  const handleClick = () => {
    setOpen((o) => !o);
  };

  const copyLink = async () => {
    let ok = false;
    try {
      await navigator.clipboard.writeText(full);
      ok = true;
    } catch (_) {
      try {
        const ta = document.createElement('textarea');
        ta.value = full;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        ok = document.execCommand('copy');
        document.body.removeChild(ta);
      } catch (_) { ok = false; }
    }
    if (ok) {
      setCopied(true);
      setTimeout(() => { setCopied(false); setOpen(false); }, 1200);
    }
  };

  const items = [
    { key: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, cls: 'text-[#25D366]', href: `https://wa.me/?text=${encodeURIComponent(full)}`, ext: true },
    { key: 'telegram', label: 'Telegram', icon: Send, cls: 'text-[#229ED9]', href: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`, ext: true },
    { key: 'sms', label: 'SMS', icon: MessageSquare, cls: 'text-tp-lime-deep', href: `sms:?&body=${encodeURIComponent(full)}`, ext: false },
    { key: 'email', label: 'Email', icon: Mail, cls: 'text-tp-coral-deep', href: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(full)}`, ext: false },
  ];

  const go = (it) => {
    if (it.ext) window.open(it.href, '_blank', 'noopener,noreferrer');
    else window.location.href = it.href;
    setTimeout(() => setOpen(false), 100);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={handleClick}
        data-testid={`${testid}-btn`}
        aria-label="Share"
        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-tp-violet text-white text-sm font-bold hover:brightness-110 active:scale-95 transition-all"
      >
        <Share2 size={15} /> Share
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-52 bg-white rounded-2xl border border-tp-violet/15 shadow-xl z-50 overflow-hidden p-1" data-testid={`${testid}-menu`}>
          {items.map((it) => (
            <button
              key={it.key}
              onClick={() => go(it)}
              data-testid={`${testid}-${it.key}`}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-tp-violet-soft/50 text-sm font-semibold text-tp-ink text-left"
            >
              <it.icon size={17} className={it.cls} /> {it.label}
            </button>
          ))}
          <button onClick={copyLink} data-testid={`${testid}-copy`} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-tp-violet-soft/50 text-sm font-semibold text-tp-ink text-left">
            {copied ? <Check size={17} className="text-tp-lime-deep" /> : <Link2 size={17} className="text-tp-violet" />} {copied ? 'Copied!' : 'Copy link'}
          </button>
          {typeof navigator !== 'undefined' && navigator.share && (
            <button onClick={() => { setOpen(false); nativeShare(); }} data-testid={`${testid}-more`} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-tp-violet-soft/50 text-sm font-semibold text-tp-ink text-left">
              <MoreHorizontal size={17} className="text-gray-500" /> More apps…
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ShareMenu;
